import React, { useCallback } from 'react';
import { Crosshair, Map } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';

export type FocusExternalFeatureParams = {
  layerId: string;
  objectId: unknown;
  title: string;
  attributes?: Record<string, unknown>;
  /** Sublayer title when the map item is a group of colliding OBJECTIDs. */
  mapLayerTitle?: string;
  lat?: unknown;
  lon?: unknown;
};

const hasObjectId = (objectId: unknown) => objectId != null && objectId !== '';

/** Shows the map and focuses one feature of an Analytics layer on it. */
export function useFocusExternalFeature() {
  const { setIsMapVisible, focusLayerFeatureOnMap } = useUscgMapContext();
  return useCallback(
    (params: FocusExternalFeatureParams) => {
      if (!hasObjectId(params.objectId)) {
        toast.info('No OBJECTID available to locate this feature on the map.');
        return;
      }
      setIsMapVisible(true);
      const lat = Number(params.lat);
      const lon = Number(params.lon);
      const hasPoint =
        params.lat != null &&
        params.lon != null &&
        Number.isFinite(lat) &&
        Number.isFinite(lon);
      void focusLayerFeatureOnMap({
        kind: 'external',
        layerId: String(params.layerId),
        featureId: String(params.objectId),
        attributes: params.attributes ?? {},
        title: params.title,
        mapLayerTitle: params.mapLayerTitle,
        ...(hasPoint
          ? {
              geometry: {
                type: 'point',
                x: lon,
                y: lat,
                spatialReference: { wkid: 4326 },
              },
            }
          : {}),
      });
    },
    [focusLayerFeatureOnMap, setIsMapVisible]
  );
}

export function MapFocusButton({
  layerId,
  exemplar,
  title,
  mapLayerTitle,
}: {
  layerId: string;
  exemplar: Record<string, unknown>;
  title: string;
  mapLayerTitle?: string;
}) {
  const focus = useFocusExternalFeature();
  return (
    <FeatureFocusButton
      disabled={!hasObjectId(exemplar.OBJECTID)}
      onClick={() =>
        focus({
          layerId,
          objectId: exemplar.OBJECTID,
          attributes: exemplar,
          title,
          mapLayerTitle,
          lat: exemplar.latitude_H3_res9_centroid ?? exemplar.latitude,
          lon: exemplar.longitude_H3_res9_centroid ?? exemplar.longitude,
        })
      }
    />
  );
}

export function FeatureFocusButton({
  disabled,
  onClick,
  label = 'Zoom to feature on map',
}: {
  disabled: boolean;
  onClick: () => void;
  label?: string;
}) {
  const text = disabled ? 'Map location unavailable' : label;
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      title={text}
      aria-label={text}
      disabled={disabled}
      onClick={onClick}
    >
      <Map className="tw:size-4" />
    </Button>
  );
}

/** Shows the map and centers it on a point. */
export function LocationFocusButton({
  location,
  label,
}: {
  location: { lat: number; lon: number } | null | undefined;
  label: string;
}) {
  const { setIsMapVisible, zoomToLocation } = useUscgMapContext();
  const text = location ? label : 'Map location unavailable';
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      title={text}
      aria-label={text}
      disabled={!location}
      onClick={() => {
        if (!location) return;
        setIsMapVisible(true);
        void zoomToLocation({
          latitude: location.lat,
          longitude: location.lon,
        });
      }}
    >
      <Crosshair className="tw:size-4" />
    </Button>
  );
}
